import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Factory, Store, Users, BarChart3, FileText, ShoppingCart, ArrowRight, ChevronRight } from "lucide-react";

const screenshots = {
  dashboard: "/images/edited_dashboard.png",
  offers: "/images/edited_offers.png",
  dealers: "/images/edited_dealers.png",
  crm: "/images/edited_crm.png",
  supplier: "/images/supplier_integration.png",
};

export function EcosystemSection() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    {
      key: "quoteFlow",
      icon: FileText,
      screenshot: screenshots.offers,
      color: "from-sky-500 to-cyan-500",
    },
    {
      key: "orderFlow",
      icon: ShoppingCart,
      screenshot: screenshots.dashboard,
      color: "from-emerald-500 to-teal-500",
    },
    {
      key: "dealerFlow",
      icon: Store,
      screenshot: screenshots.dealers,
      color: "from-amber-500 to-orange-500",
    },
    {
      key: "supplierFlow",
      icon: Factory,
      screenshot: screenshots.supplier,
      color: "from-violet-500 to-purple-500",
    },
    {
      key: "crmFlow",
      icon: Users,
      screenshot: screenshots.crm,
      color: "from-rose-500 to-pink-500",
    },
  ];

  const suppliers = [
    { name: t("ecosystem.supplier") + " A", sub: "Hammadde" },
    { name: t("ecosystem.supplier") + " B", sub: "Aksesuar" },
    { name: t("ecosystem.supplier") + " C", sub: "Kumaş" },
  ];

  const dealers = [
    { name: "Yıldız Mobilya", sub: "Istanbul" },
    { name: "Ege Dekor", sub: "Izmir" },
    { name: "Karadeniz Grup", sub: "Trabzon" },
  ];

  return (
    <section data-testid="ecosystem-section" className="py-20 sm:py-28 bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:3rem_3rem]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#22A7D1]/8 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#22A7D1]">
            {t("ecosystem.overline")}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-bold text-white">
            {t("ecosystem.title")}
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-400">
            {t("ecosystem.subtitle")}
          </p>
        </div>

        {/* Ecosystem Flow Diagram */}
        <div className="grid grid-cols-1 lg:grid-cols-7 gap-4 lg:gap-6 items-start mb-16">
          {/* Suppliers */}
          <div className="lg:col-span-2" data-testid="ecosystem-suppliers">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{t("ecosystem.suppliers")}</div>
            <div className="space-y-2">
              {suppliers.map((s, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-700/50 hover:border-orange-500/30 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                    <Factory className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-200">{s.name}</div>
                    <div className="text-[10px] text-slate-500">{s.sub}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center my-3">
              <div className="flex items-center gap-1 text-orange-400">
                <span className="text-xs font-medium">{t("ecosystem.getQuote")}</span>
                <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </div>

          {/* Center Panel */}
          <div className="lg:col-span-3" data-testid="ecosystem-panel">
            <div className="rounded-2xl bg-gradient-to-b from-[#22A7D1]/15 to-[#32AED6]/5 border border-[#22A7D1]/30 p-6 text-center">
              <img
                src="/images/entegrelogo/EP-Logo-Normal.svg"
                alt={t("imageAlt.logo")}
                width="160"
                height="32"
                className="h-8 mx-auto mb-2"
                loading="lazy"
              />
              <div className="text-sm font-bold text-white mb-1">{t("ecosystem.panelTitle")}</div>
              <p className="text-xs text-slate-400 mb-4">{t("ecosystem.panelDesc")}</p>

              {/* Module chips */}
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {[t("ecosystem.quote"), t("ecosystem.order"), t("ecosystem.crm"), "SSH", "ERP"].map((m, i) => (
                  <span key={i} className="px-3 py-1 rounded-full bg-slate-800/80 text-[10px] font-medium text-slate-300 border border-slate-700/50">{m}</span>
                ))}
              </div>

              {/* Flow arrows */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: t("ecosystem.dataSync"), icon: "↔", color: "text-violet-400" },
                  { label: t("ecosystem.sendQuote"), icon: "→", color: "text-sky-400" },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700/30">
                    <span className={`text-sm font-bold ${f.color}`}>{f.icon}</span>
                    <span className="text-[10px] text-slate-400">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Dealers & Customers */}
          <div className="lg:col-span-2" data-testid="ecosystem-dealers">
            <div className="flex justify-center mb-3 lg:hidden">
              <div className="flex items-center gap-1 text-sky-400">
                <ArrowRight className="w-3 h-3" />
                <span className="text-xs font-medium">{t("ecosystem.sendQuote")}</span>
              </div>
            </div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{t("ecosystem.dealersBranches")}</div>
            <div className="space-y-2">
              {dealers.map((d, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-700/50 hover:border-sky-500/30 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
                    <Store className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-200">{d.name}</div>
                    <div className="text-[10px] text-slate-500">{d.sub}</div>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-700/50">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <div className="text-sm font-medium text-slate-200">{t("ecosystem.customers")}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Tabs - Screenshot Showcase */}
        <div className="rounded-2xl border border-slate-700/50 bg-slate-800/40 overflow-hidden">
          {/* Tab buttons */}
          <div className="flex overflow-x-auto border-b border-slate-700/50 no-scrollbar">
            {tabs.map((tab, i) => {
              const Icon = tab.icon;
              return (
                <button
                  key={i}
                  onClick={() => setActiveTab(i)}
                  className={`flex items-center gap-2 px-4 sm:px-6 py-4 text-xs sm:text-sm font-medium whitespace-nowrap transition-all border-b-2 ${
                    activeTab === i
                      ? "text-white border-[#22A7D1] bg-slate-800/60"
                      : "text-slate-500 border-transparent hover:text-slate-300 hover:bg-slate-800/30"
                  }`}
                  data-testid={`ecosystem-tab-${tab.key}`}
                >
                  <Icon className="w-4 h-4" />
                  {t(`ecosystem.${tab.key}`)}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="p-4 sm:p-6">
            <div className="grid lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-1 space-y-3">
                <h3 className="text-lg font-bold text-white">
                  {t(`ecosystem.${tabs[activeTab].key}`)}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {t(`ecosystem.${tabs[activeTab].key}Desc`)}
                </p>
                <ul className="space-y-2">
                  {(t(`ecosystem.${tabs[activeTab].key}Points`) || []).map((point, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <ChevronRight className="w-4 h-4 text-[#22A7D1] mt-0.5 shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-2 rounded-xl overflow-hidden border border-slate-700/50">
                <img
                  src={tabs[activeTab].screenshot}
                  alt={`EntegrePanel ${t(`ecosystem.${tabs[activeTab].key}`)} - B2B Bayi Yönetim`}
                  width="800"
                  height="500"
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
